/**
 * ブログ記事目次生成機能
 * ブログ記事本文内の最初のH2タグの直上に目次を自動生成します
 */

class BlogArticleTOC {
  constructor() {
    this.contentContainer = null;
    this.headings = [];
    this.firstH2 = null;
  }

  /**
   * 初期化処理
   */
  init() {
    // .blog-post-content 要素を検索
    this.contentContainer = document.querySelector('.blog-post-content');
    
    if (!this.contentContainer) {
      return; // ブログ記事ページでない場合は終了
    }

    // 見出しタグを取得
    this.headings = this.getHeadings();
    
    // H2が存在しない場合は終了
    if (this.headings.length === 0 || !this.hasH2()) {
      return;
    }

    // 最初のH2タグを特定
    this.firstH2 = this.findFirstH2();
    
    if (!this.firstH2) {
      return;
    }

    // 見出しにIDを付与
    this.addIdsToHeadings();

    // 見出しにスクロールマージンを設定（ヘッダー分のオフセット）
    this.setScrollMargin();

    // 目次を生成して挿入
    this.insertTOC();
  }

  /**
   * 見出しタグ（H2-H6）を取得
   */
  getHeadings() {
    const selector = 'h2, h3, h4, h5, h6';
    const allHeadings = Array.from(this.contentContainer.querySelectorAll(selector));
    
    // .blog-post-content 内の直接の子要素または子孫要素をフィルタリング
    return allHeadings.filter(heading => {
      return this.contentContainer.contains(heading);
    });
  }

  /**
   * H2タグが存在するかチェック
   */
  hasH2() {
    return this.headings.some(heading => heading.tagName === 'H2');
  }

  /**
   * 最初のH2タグを検出
   */
  findFirstH2() {
    return this.headings.find(heading => heading.tagName === 'H2');
  }

  /**
   * 見出しにIDを付与（アンカーリンク用）
   */
  addIdsToHeadings() {
    this.headings.forEach((heading, index) => {
      // 既にIDが設定されている場合はスキップ
      if (heading.id) {
        return;
      }

      // IDを生成（テキストから生成、重複時はインデックスを追加）
      const text = heading.textContent.trim();
      let id = this.generateId(text);
      
      // 重複チェック
      let counter = 0;
      let uniqueId = id;
      while (document.getElementById(uniqueId)) {
        counter++;
        uniqueId = `${id}-${counter}`;
      }
      
      heading.id = uniqueId;
    });
  }

  /**
   * テキストからIDを生成
   */
  generateId(text) {
    // 日本語や特殊文字を処理
    return text
      .toLowerCase()
      .replace(/[^\w\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50); // 長さ制限
  }

  /**
   * ヘッダーとアナウンスメントバーの合計高さを取得
   */
  getHeaderHeight() {
    let totalHeight = 0;
    
    // アナウンスメントバーの高さを取得
    const announcementBar = document.querySelector('.announcement-bar');
    if (announcementBar && announcementBar instanceof HTMLElement) {
      totalHeight += announcementBar.offsetHeight;
    }
    
    // ヘッダーグループの高さを取得
    const headerGroup = document.getElementById('header-group');
    if (headerGroup) {
      const headerGroupHeight = headerGroup.offsetHeight;
      
      // アナウンスメントバーがヘッダーグループ内にあるかチェック
      if (announcementBar && announcementBar instanceof HTMLElement && headerGroup.contains(announcementBar)) {
        // アナウンスメントバーがヘッダーグループ内にある場合は、ヘッダーグループ全体の高さを使用
        totalHeight = headerGroupHeight;
      } else if (announcementBar && announcementBar instanceof HTMLElement) {
        // アナウンスメントバーがヘッダーグループ外にある場合は、両方の高さを合計
        totalHeight = announcementBar.offsetHeight + headerGroupHeight;
      } else {
        // アナウンスメントバーがない場合は、ヘッダーグループのみ
        totalHeight = headerGroupHeight;
      }
    } else if (announcementBar && announcementBar instanceof HTMLElement) {
      // ヘッダーグループが見つからないが、アナウンスメントバーがある場合
      totalHeight = announcementBar.offsetHeight;
    }
    
    // フォールバック: 固定ヘッダーとアナウンスメントバーの一般的な高さを想定
    if (totalHeight === 0) {
      return 80; // デフォルト値（px）
    }
    
    return totalHeight;
  }

  /**
   * 見出しにスクロールマージンを設定（ヘッダー分のオフセット）
   */
  setScrollMargin() {
    const headerHeight = this.getHeaderHeight();
    // 少し余裕を持たせる（10px）
    const scrollMargin = headerHeight + 10;

    this.headings.forEach(heading => {
      heading.style.scrollMarginTop = `${scrollMargin}px`;
    });
  }

  /**
   * 目次を生成して挿入
   */
  insertTOC() {
    const tocElement = this.createTOC();
    
    // 最初のH2タグの直前に挿入
    this.firstH2.parentNode.insertBefore(tocElement, this.firstH2);
  }

  /**
   * 目次要素を生成
   */
  createTOC() {
    const tocContainer = document.createElement('div');
    tocContainer.className = 'blog-post-toc';

    // タイトル
    const title = document.createElement('h2');
    title.className = 'blog-post-toc__title';
    title.textContent = '目次';

    // ナビゲーション
    const nav = document.createElement('nav');
    nav.className = 'blog-post-toc__nav';
    nav.setAttribute('aria-label', '目次');

    // リスト
    const list = document.createElement('ul');
    list.className = 'blog-post-toc__list';

    // 見出しから目次項目を生成
    this.headings.forEach(heading => {
      const item = this.createTOCItem(heading);
      if (item) {
        list.appendChild(item);
      }
    });

    nav.appendChild(list);
    tocContainer.appendChild(title);
    tocContainer.appendChild(nav);

    return tocContainer;
  }

  /**
   * 目次項目を生成
   */
  createTOCItem(heading) {
    if (!heading.id) {
      return null;
    }

    const level = parseInt(heading.tagName.charAt(1)); // H2 -> 2, H3 -> 3, etc.
    const text = heading.textContent.trim();

    if (!text) {
      return null;
    }

    const listItem = document.createElement('li');
    listItem.className = `blog-post-toc__item blog-post-toc__item--level-${level}`;

    const link = document.createElement('a');
    link.href = `#${heading.id}`;
    link.textContent = text;
    link.className = 'blog-post-toc__link';

    // スムーズスクロール（ヘッダー分のオフセットを考慮）
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const targetElement = document.getElementById(heading.id);
      if (targetElement) {
        const headerHeight = this.getHeaderHeight();
        const offset = headerHeight + 10; // 少し余裕を持たせる
        
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        // URLを更新（履歴に追加しない）
        window.history.replaceState(null, '', `#${heading.id}`);
      }
    });

    listItem.appendChild(link);
    return listItem;
  }
}

// DOMContentLoaded後に実行
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const toc = new BlogArticleTOC();
    toc.init();
  });
} else {
  // 既にDOMが読み込まれている場合
  const toc = new BlogArticleTOC();
  toc.init();
}